import { ErrorOutlineOutlined } from '@mui/icons-material'
import { Card, CardActionArea, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

export enum WebsiteMissingOption {
  AutoFill = 'autoFill',
  Manual = 'manual',
  Skip = 'skip',
}

type WebsiteMissingChoiceStepProps = {
  missingCount: number
  selectedOption: WebsiteMissingOption | null
  onOptionChange: (option: WebsiteMissingOption) => void
}

const OPTIONS = [
  {
    value: WebsiteMissingOption.AutoFill,
    titleId: 'form.scheduleProviderScreening.websiteChoice.autoFill',
    descriptionId: 'form.scheduleProviderScreening.websiteChoice.autoFillDescription',
  },
  {
    value: WebsiteMissingOption.Manual,
    titleId: 'form.scheduleProviderScreening.websiteChoice.manual',
    descriptionId: 'form.scheduleProviderScreening.websiteChoice.manualDescription',
  },
  {
    value: WebsiteMissingOption.Skip,
    titleId: 'form.scheduleProviderScreening.websiteChoice.skip',
    descriptionId: 'form.scheduleProviderScreening.websiteChoice.skipDescription',
  },
]

const WebsiteMissingChoiceStep: React.FC<WebsiteMissingChoiceStepProps> = ({
  missingCount,
  selectedOption,
  onOptionChange,
}) => {
  const { formatMessage } = useIntl()

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} alignItems="center">
        <ErrorOutlineOutlined sx={{ color: 'semantic.error' }} />
        <Typography fontWeight={600}>
          {formatMessage({ id: 'form.scheduleProviderScreening.websiteChoice.information' }, { count: missingCount })}
        </Typography>
      </Stack>

      <Typography color="textSecondary">
        {formatMessage({ id: 'form.scheduleProviderScreening.websiteChoice.description' })}
      </Typography>

      <Typography fontWeight={600}>
        {formatMessage({ id: 'form.scheduleProviderScreening.websiteChoice.selectLabel' })}
      </Typography>

      <RadioGroup value={selectedOption ?? ''} onChange={e => onOptionChange(e.target.value as WebsiteMissingOption)}>
        <Stack spacing={2}>
          {OPTIONS.map(option => (
            <Card
              key={option.value}
              elevation={0}
              variant="outlined"
              sx={{
                borderColor: selectedOption === option.value ? 'primary.main' : 'divider',
                borderWidth: selectedOption === option.value ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => onOptionChange(option.value)} sx={{ p: 2 }}>
                <FormControlLabel
                  value={option.value}
                  control={<Radio checked={selectedOption === option.value} />}
                  label={
                    <Stack pl={2}>
                      <Typography variant="subtitle1">{formatMessage({ id: option.titleId })}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatMessage({ id: option.descriptionId })}
                      </Typography>
                    </Stack>
                  }
                  sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                />
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      </RadioGroup>
    </Stack>
  )
}

export default WebsiteMissingChoiceStep
